import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, useMotionValue, useTransform, useSpring, useScroll, useInView } from "framer-motion";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ArrowRight, Play, Shield, Zap, Cpu, Wifi, Lock, Activity } from "lucide-react";

// replace these imports/paths with your own assets
import heroImg from "../assets/c11.jpg";
import slide1 from "../assets/c11.jpg";
import slide2 from "../assets/c22.jpg";
import slide3 from "../assets/c33.jpg";
import cover1 from "../assets/c44.jpg";
import cover2 from "../assets/c55.jpg";
import cover3 from "../assets/c66.jpg";

// Animated Counter Component
function AnimatedCounter({ value, duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(value);
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
}

// Feature Card Component
function FeatureCard({ icon: Icon, title, description, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, type: "spring", stiffness: 100 }}
      whileHover={{ y: -10, scale: 1.02 }}
      className="group relative bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative z-10">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/30">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-white/70 leading-relaxed">{description}</p>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
    </motion.div>
  );
}

// 3D Card Component
function Card3D({ children, className = "" }) {
  const ref = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(mouseY, [-100, 100], [8, -8]);
  const rotateY = useTransform(mouseX, [-100, 100], [-8, 8]);
  const springRotateX = useSpring(rotateX, { stiffness: 300, damping: 30 });
  const springRotateY = useSpring(rotateY, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX: springRotateX, rotateY: springRotateY, perspective: 1000 }}
      className={`transform preserve-3d ${className}`}
    >
      {children}
    </motion.div>
  );
}

// Particle Network Component
function ParticleNetwork() {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);

    const particles = [];
    const connectionDistance = 150;
    const PARTICLE_COUNT = Math.floor((w * h) / 15000);

    class Particle {
      constructor() {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 2 + 1;
        this.color = `hsla(${Math.random() * 60 + 220}, 80%, 70%, ${Math.random() * 0.5 + 0.3})`;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > w) this.vx *= -1;
        if (this.y < 0 || this.y > h) this.vy *= -1;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push(new Particle());
    }

    function onResize() {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    }
    window.addEventListener("resize", onResize);

    function animate() {
      ctx.clearRect(0, 0, w, h);
      
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = 1 - distance / connectionDistance;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(99, 102, 241, ${opacity * 0.3})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      animationRef.current = requestAnimationFrame(animate);
    }

    animate();
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 -z-10 pointer-events-none" />;
}

// Animated Background Gradient
function AnimatedGradient() {
  return (
    <div className="fixed inset-0 -z-20 pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900/40 via-transparent to-transparent" />
      <motion.div
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 opacity-30 bg-[linear-gradient(45deg,transparent_25%,rgba(99,102,241,0.1)_50%,transparent_75%,transparent_100%)] bg-[length:400%_400%]"
      />
    </div>
  );
}

// Floating Shape Component
function FloatingShape({ delay, duration, size, color, position }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: [0.1, 0.3, 0.1], scale: [1, 1.1, 1] }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
      className={`absolute rounded-full blur-xl pointer-events-none ${size} ${color}`}
      style={{ ...position }}
    />
  );
}

export default function SplashScreen() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  const features = [
    { icon: Wifi, title: "IoT Connectivity", description: "Real-time monitoring and control through cloud-based IoT platform with 99.9% uptime." },
    { icon: Shield, title: "Smart Safety", description: "Advanced collision detection, emergency brakes, and biometric authentication systems." },
    { icon: Activity, title: "Predictive Maintenance", description: "AI-driven diagnostics that predict issues before they occur, reducing downtime by 60%." },
    { icon: Cpu, title: "AI Control", description: "Intelligent traffic management and energy optimization using machine learning algorithms." },
    { icon: Zap, title: "Fast Installation", description: "Modular design allows installation in 30% less time than traditional elevators." },
    { icon: Lock, title: "Cyber Security", description: "End-to-end encryption and secure authentication for all connected systems." },
  ];

  const stats = [
    { value: "500+", label: "Installations" },
    { value: "99.9%", label: "Uptime" },
    { value: "50%", label: "Energy Saved" },
    { value: "24/7", label: "Support" },
  ];

  const sliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    speed: 800,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (_, next) => setActiveSlide(next),
    customPaging: (i) => (
      <div className="w-2 h-2 rounded-full bg-white/30 mt-4 transition-all duration-300" />
    ),
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } },
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 z-50" style={{ scaleX, transformOrigin: "0%" }} />

      {/* Animated Backgrounds */}
      <AnimatedGradient />
      <ParticleNetwork />

      {/* Floating Shapes */}
      <FloatingShape delay={0} duration={8} size="w-96 h-96" color="bg-indigo-500/20" position={{ top: "10%", left: "5%" }} />
      <FloatingShape delay={2} duration={10} size="w-80 h-80" color="bg-purple-500/20" position={{ top: "30%", right: "10%" }} />
      <FloatingShape delay={4} duration={12} size="w-72 h-72" color="bg-cyan-500/15" position={{ bottom: "20%", left: "15%" }} />
      <FloatingShape delay={1} duration={9} size="w-64 h-64" color="bg-pink-500/15" position={{ bottom: "30%", right: "5%" }} />

      {/* SECTOR 1: Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 md:px-8 pt-20">
        <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/20 border border-indigo-500/30">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm font-medium text-indigo-300">Next-Generation Elevators</span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-transparent">
                Elevating
              </span>
              <br />
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Excellence
              </span>
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg text-white/70 max-w-xl leading-relaxed">
              Experience the future of vertical transportation with our AI-powered, 
              IoT-connected elevator systems designed for safety, efficiency, and elegance.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
              <motion.button
                onClick={() => navigate("/login")}
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(99, 102, 241, 0.4)" }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full font-semibold text-white overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get Started <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>

              <motion.button
                onClick={() => setShowModal(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm font-medium hover:bg-white/10 transition-colors"
              >
                Learn More
              </motion.button>
            </motion.div>

            {/* Stats */}
            <motion.div variants={itemVariants} className="grid grid-cols-4 gap-6 pt-8 border-t border-white/10">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                    <AnimatedCounter value={stat.value.replace(/\D/g, "")} />
                    {stat.value.includes("%") ? "%" : "+"}
                  </div>
                  <div className="text-xs text-white/50 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - 3D Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <Card3D className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                <img
                  src={heroImg}
                  alt="Elevator"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                
                {/* Floating Elements */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute top-6 right-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    System Online
                  </span>
                </motion.div>

                {/* Decorative Covers */}
                <div className="absolute -bottom-6 -left-6 flex gap-3">
                  <motion.img
                    src={cover1}
                    alt="cover1"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    className="w-20 h-24 object-cover rounded-xl shadow-lg border-2 border-white/20"
                  />
                  <motion.img
                    src={cover2}
                    alt="cover2"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-24 h-28 object-cover rounded-xl shadow-lg border-2 border-white/20 -mt-4"
                  />
                  <motion.img
                    src={cover3}
                    alt="cover3"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    className="w-20 h-24 object-cover rounded-xl shadow-lg border-2 border-white/20"
                  />
                </div>
              </div>
            </Card3D>

            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-3xl blur-3xl -z-10" />
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-sm text-white/50">Scroll to explore</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-white"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* SECTOR 2: Features Grid */}
      <section className="relative py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Intelligent Features
              </span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              Powered by cutting-edge technology for unmatched performance and safety
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTOR 3: Showcase Section */}
      <section className="relative py-32 px-4 md:px-8 bg-gradient-to-b from-transparent via-indigo-950/20 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Slider */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                <Slider {...sliderSettings}>
                  {[slide1, slide2, slide3].map((src, index) => (
                    <div key={index} className="relative">
                      <img src={src} alt={`slide${index}`} className="w-full h-96 object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                            <Play className="w-5 h-5 text-white ml-1" />
                          </div>
                          <div>
                            <h4 className="font-semibold">Installation Demo {index + 1}</h4>
                            <p className="text-sm text-white/70">Watch our smart elevator in action</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-4xl font-bold">
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                  See It In Action
                </span>
              </h2>
              <p className="text-white/70 leading-relaxed">
                Explore our real-world installations and see how our elevators transform 
                buildings with intelligent automation and stunning design.
              </p>

              {/* Video Grid */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { id: "ysz5S6PUM-U", title: "Smart Control" },
                  { id: "jNQXAC9IVRw", title: "Safety Demo" },
                  { id: "iNJdPyoqt8U", title: "Speed Test" },
                  { id: "lTTajzrSkCw", title: "Design Tour" },
                ].map((video, index) => (
                  <motion.button
                    key={index}
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${video.id}`, "_blank")}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative rounded-xl overflow-hidden shadow-lg aspect-video"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${video.id}/0.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                      <Play className="w-10 h-10 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <span className="text-sm font-medium">{video.title}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTOR 4: Technology Showcase */}
      <section className="relative py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-4xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                  Advanced Technology
                </span>
              </h2>
              <p className="text-white/70 mb-8 leading-relaxed">
                Our elevators feature state-of-the-art technology including AI-powered 
                traffic management, predictive maintenance algorithms, and seamless 
                IoT integration for smart building systems.
              </p>

              <div className="space-y-4">
                {[
                  "Machine learning-based traffic optimization",
                  "Real-time health monitoring and diagnostics",
                  "Touchless destination control systems",
                  "Energy recovery during descent",
                  "Advanced voice control integration",
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-indigo-400" />
                    </div>
                    <span className="text-white/80">{item}</span>
                  </motion.div>
                ))}
              </div>

              <motion.button
                onClick={() => navigate("/contact")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="mt-10 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full font-semibold flex items-center gap-2"
              >
                Explore Technology <ArrowRight className="w-5 h-5" />
              </motion.button>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <Card3D>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                  <img src={heroImg} alt="Technology" className="w-full h-[500px] object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/30 to-purple-500/30 mix-blend-overlay" />
                  
                  {/* Floating Badge */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute top-6 left-6 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20"
                  >
                    <div className="flex items-center gap-2">
                      <Cpu className="w-5 h-5 text-indigo-400" />
                      <span className="font-medium">AI Powered</span>
                    </div>
                  </motion.div>
                </div>
              </Card3D>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTOR 5: CTA Section */}
      <section className="relative py-32 px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
          <div className="relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 p-12 md:p-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Building?
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
              Get a personalized quote and discover how our smart elevators 
              can enhance your property's value and user experience.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <motion.button
                onClick={() => navigate("/login")}
                whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(99, 102, 241, 0.4)" }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full font-semibold text-lg"
              >
                Get Free Quote
              </motion.button>
              <motion.button
                onClick={() => navigate("/contact")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 rounded-full border border-white/20 bg-white/5 font-medium text-lg hover:bg-white/10 transition-colors"
              >
                Contact Sales
              </motion.button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <img src={heroImg} alt="logo" className="w-10 h-10 rounded-lg object-cover" />
            <span className="font-semibold">Unique Elevator</span>
          </div>
          <p className="text-sm text-white/50">© 2024 Unique Elevator. All rights reserved.</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-sm text-white/60 hover:text-white transition-colors"
          >
            Terms & Privacy
          </button>
        </div>
      </footer>

      {/* Terms Modal */}
      {showModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-slate-900/95 backdrop-blur-xl rounded-3xl border border-white/10 p-8"
          >
            <h3 className="text-2xl font-bold mb-6">Terms & Conditions</h3>
            <div className="max-h-[50vh] overflow-y-auto pr-4 space-y-4 text-white/70">
              <p><strong>• Usage:</strong> By proceeding you accept our policies and consent to collection of operational metrics.</p>
              <p><strong>• Safety:</strong> Do not attempt to tamper with safety systems or sensors.</p>
              <p><strong>• Privacy:</strong> We respect your data privacy and comply with all applicable regulations.</p>
              <p><strong>• Warranty:</strong> All elevators come with comprehensive warranty coverage as outlined in your service agreement.</p>
              <p><strong>• Changes:</strong> We may update our features or terms periodically — keep app updated.</p>
              <p>… Full legal terms available in our documentation …</p>
            </div>
            <div className="mt-6 flex items-center justify-between gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="w-5 h-5 rounded border-white/30 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-white/90">I accept the terms</span>
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { if (accepted) navigate("/"); }}
                  disabled={!accepted}
                  className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                    accepted
                      ? "bg-gradient-to-r from-indigo-500 to-purple-500 hover:shadow-lg hover:shadow-indigo-500/30"
                      : "bg-white/10 cursor-not-allowed"
                  }`}
                >
                  Continue to App
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
